import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  GraduationCap,
  FileText,
  HelpCircle,
  Archive,
  Settings,
  BarChart3
} from 'lucide-react';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  // We need the location hook for NavLink to work properly
  useLocation();

  const trigger = useRef<HTMLButtonElement>(null);
  const sidebar = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target as Node) ||
        trigger.current.contains(target as Node)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  return (
    <div>
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-primary/50 z-40 lg:hidden lg:z-auto transition-opacity duration-200 backdrop-blur-sm ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
        onClick={() => setSidebarOpen(false)} // Close sidebar when backdrop is clicked
      ></div>

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`sidebar flex flex-col fixed z-50 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-screen overflow-y-auto no-scrollbar w-64 shrink-0 bg-primary p-4 transition-all duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-64'
        }`}
      >
        {/* Sidebar header */}
        <div className="flex items-center mb-8">
          {/* Logo */}
          <NavLink to="/" className="flex items-center space-x-3">
            <img src="/logo.svg" alt="BayesPrep Admin" className="h-8 w-8" />
            <div className="flex flex-col">
              <span className="text-white font-bold text-lg leading-tight">BayesPrep</span>
              <span className="text-white/80 text-sm font-medium leading-tight">Admin</span>
            </div>
          </NavLink>
          {/* Close button */}
          <button
            ref={trigger}
            className="lg:hidden text-white hover:text-white ml-auto p-2 rounded-full hover:bg-white/10"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              setSidebarOpen(false);
              console.log('Close sidebar button clicked');
            }}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
        </div>

        {/* Links */}
        <div className="space-y-1">
          <div className="mb-4">
            <p className="text-xs uppercase text-white/70 font-semibold px-3 mb-2">
              PAGES
            </p>
            <ul>
              {/* Dashboard */}
              <li className="mb-0.5">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <LayoutDashboard className="shrink-0 h-5 w-5 mr-3" />
                  Dashboard
                </NavLink>
              </li>
              {/* Students */}
              <li className="mb-0.5">
                <NavLink
                  to="/students"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <Users className="shrink-0 h-5 w-5 mr-3" />
                  Students
                </NavLink>
              </li>
              {/* Classes */}
              <li className="mb-0.5">
                <NavLink
                  to="/classes"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <GraduationCap className="shrink-0 h-5 w-5 mr-3" />
                  Classes
                </NavLink>
              </li>
              {/* Tests */}
              <li className="mb-0.5">
                <NavLink
                  to="/tests"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <FileText className="shrink-0 h-5 w-5 mr-3" />
                  Tests
                </NavLink>
              </li>
              {/* Test Results */}
              <li className="mb-0.5">
                <NavLink
                  to="/tests/results"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <BarChart3 className="shrink-0 h-5 w-5 mr-3" />
                  Test Results
                </NavLink>
              </li>
              {/* Questions */}
              <li className="mb-0.5">
                <NavLink
                  to="/questions"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <HelpCircle className="shrink-0 h-5 w-5 mr-3" />
                  Questions
                </NavLink>
              </li>
              {/* Archive */}
              <li className="mb-0.5">
                <NavLink
                  to="/archive"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <Archive className="shrink-0 h-5 w-5 mr-3" />
                  Archive
                </NavLink>
              </li>
              {/* Settings */}
              <li className="mb-0.5">
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2.5 text-sm font-medium text-white rounded-md ${
                      isActive ? 'bg-white/20' : 'hover:bg-white/10'
                    }`
                  }
                >
                  <Settings className="shrink-0 h-5 w-5 mr-3" />
                  Settings
                </NavLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
